import { type FC, useCallback, useMemo, useRef, useState } from 'react';

import Banner from '@/components/UI/Banner';
import Switch from '@/components/UI/FormElements/Switch/Switch';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTable';
import type {
  DosingMatrixRow,
  DosingMatrixRowKey,
  DosingSessionDraft,
  TreatmentOption,
} from '@/components/Samples/SamplePlanTable/Execute/ExecuteSamplesSetDosingStep.utils';
import {
  blankCalculationBaseWarning,
  blankRowNames,
  createSessionId,
  sessionNameError,
} from '@/components/Samples/SamplePlanTable/Execute/ExecuteSamplesSetDosingStep.utils';
import { errorToast } from '@/helpers';
import { _isNil, _isNotBlank, _isNotEmpty, _notNil } from '@/littledash';
import useElementBinding from '@/support/Hooks/utils/useElementBinding';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';

import { buildGridRows, collectGridTreatments, type DosingSessionGridRow } from './DosingSession.utils';
import DosingSessionTable from './DosingSessionTable';

export interface DosingSessionModalProps {
  /** Session being edited; absent when adding a new one. */
  session?: DosingSessionDraft;
  /** Every session on the plan, used to keep session names unique. */
  sessions: Array<DosingSessionDraft>;
  rows: Array<DosingMatrixRow>;
  rowOptions: Map<DosingMatrixRowKey, Array<TreatmentOption>>;
  defaultName: string;
  onSave: (session: DosingSessionDraft) => void;
  closeModal: () => void;
}

/**
 * Authors a single dosing session: a name, whether collection times are calculated from it, and one
 * treatment per study group. Performs no I/O — the whole plan is submitted with the sampling sheet,
 * so the session is handed back to the wizard step as a draft.
 */
const DosingSessionModal: FC<DosingSessionModalProps> = ({
  session,
  sessions,
  rows,
  rowOptions,
  defaultName,
  onSave,
  closeModal,
}) => {
  const [name, setName] = useState(session?.name ?? defaultName);
  const [nameTouched, setNameTouched] = useState(false);
  const [isCalculationBase, setIsCalculationBase] = useState(session?.isCalculationBase ?? sessions.length === 0);
  const hotRef = useRef<HotTableRef | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useElementBinding({
    field: 'hot',
    element: containerRef,
    accessor: useCallback(() => hotRef.current?.hotInstance, []),
  });

  const gridRows = useMemo(() => buildGridRows(rows, rowOptions, session), [rows, rowOptions, session]);
  const getTreatmentOptions = useCallback((rowKey: DosingMatrixRowKey) => rowOptions.get(rowKey) ?? [], [rowOptions]);

  const [blankRows, setBlankRows] = useState(() =>
    blankRowNames(rows, collectGridTreatments(gridRows, rows, rowOptions))
  );

  const handleGridChange = useCallback(() => {
    const sourceData = hotRef.current?.hotInstance?.getSourceData() as Array<DosingSessionGridRow> | undefined;
    if (_notNil(sourceData)) {
      setBlankRows(blankRowNames(rows, collectGridTreatments(sourceData, rows, rowOptions)));
    }
  }, [rows, rowOptions]);

  const nameError = sessionNameError(name, sessions, session?.id);
  const trimmedName = name.trim();

  const handleSave = useCallback(() => {
    if (_notNil(nameError)) {
      setNameTouched(true);
      return;
    }

    const hotInstance = hotRef.current?.hotInstance;
    if (_isNil(hotInstance)) {
      errorToast('Unable to access table data');
      return;
    }

    const sourceData = hotInstance.getSourceData() as Array<DosingSessionGridRow>;
    onSave({
      id: session?.id ?? createSessionId(),
      name: name.trim(),
      isCalculationBase,
      treatments: collectGridTreatments(sourceData, rows, rowOptions),
    });
    closeModal();
  }, [nameError, session, name, isCalculationBase, rows, rowOptions, onSave, closeModal]);

  return (
    <ModalContainer size="medium" className="bg-white" testKey="DosingSessionModal">
      <ModalHeader
        title={_isNil(session) ? 'Add dosing session' : 'Edit dosing session'}
        closeModal={closeModal}
        className="pa3 bb b--moon-gray w-100"
      />

      <div className="pa3" ref={containerRef} data-testid="dosing-session__body">
        <label htmlFor="dosing-session-name">Session name</label>
        <input
          type="text"
          id="dosing-session-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          onBlur={() => setNameTouched(true)}
          style={{ marginBottom: 0 }}
          data-testid="dosing-session__name-input"
          autoFocus
        />
        {nameTouched && _notNil(nameError) && (
          <small className="red db pt2" data-testid="dosing-session__name-error">
            {nameError}
          </small>
        )}

        <Banner info dismiss={false} className="mt3">
          <div className="flex justify-between items-center">
            <div className="mr3">
              <h3 className="f5 normal lh-title">Use for collection-time calculation</h3>
              <p className="f6 pt2 lh-copy">
                Sample collection times on the sheet are calculated from this session&apos;s dosing time. Only one
                session can be the basis.
              </p>
            </div>
            <Switch
              className="flex-none"
              value={isCalculationBase}
              onChange={setIsCalculationBase}
              ariaLabel="Use for collection-time calculation"
              testId="dosing-session__calculation-base"
            />
          </div>
        </Banner>

        {isCalculationBase && _isNotEmpty(blankRows) && (
          <Banner warning dismiss={false} className="mt3" testId="dosing-session__blank-treatments-warning">
            <p className="f6 lh-copy">
              {blankCalculationBaseWarning(_isNotBlank(trimmedName) ? trimmedName : 'this session', blankRows)}
            </p>
          </Banner>
        )}

        <div className="mt3">
          <DosingSessionTable
            rows={rows}
            gridRows={gridRows}
            getTreatmentOptions={getTreatmentOptions}
            sessionName={name}
            onGridChange={handleGridChange}
            innerRef={hotRef}
          />
        </div>
      </div>

      <ModalActions
        className="pa3 bt b--moon-gray w-100"
        onSubmit={handleSave}
        onCancel={closeModal}
        submitBtnText="Save"
        cancelBtnText="Cancel"
        submitButtonProps={{ disabled: _notNil(nameError), testId: 'dosing-session__submit' }}
        cancelButtonProps={{ testId: 'dosing-session__cancel' }}
      />
    </ModalContainer>
  );
};

export default DosingSessionModal;
